/**
 * Rise + Rest — Waitlist landing page controller.
 *
 * The section-to-section "cover" effect (next section slides up over the
 * previous one on scroll, and only pins once it has fully covered it) is
 * plain CSS `position:sticky` — ported directly from both source
 * prototypes, which use exactly this technique (confirmed: every pinned
 * section is `position:sticky;top:0`, followed by a plain non-sticky
 * spacer — `[data-rit-anchor]` / `[data-proto-anchor]` — that reserves the
 * extra scroll distance). GSAP's ScrollTrigger `pin:true` was tried first
 * but is the wrong primitive here: it detaches the section into a
 * synthetic fixed position via a pin-spacer, so two independently pinned
 * sections can only abruptly swap, never visually overlap/slide over one
 * another the way real sibling `position:sticky` elements naturally do.
 * GSAP is not used at all any more: the `[data-reveal]` entrance
 * micro-animations it was kept for are now plain CSS transitions driven by
 * an IntersectionObserver, ported verbatim from the prototypes — see
 * `_bindReveals` for why GSAP was actively wrong for that job.
 *
 * The internal per-section scroll animations (Ritual day/night crossfade,
 * Protocol 4-step carousel) are hand-rolled scrollY math ported verbatim
 * from the prototypes' own `_update()`, read off the non-sticky anchor
 * spacers rather than the sticky sections themselves — a *stuck*
 * `position:sticky` element reports a moving `offsetTop`/`getBoundingClientRect`
 * that tracks scroll position instead of its original static position,
 * which corrupts any math computed directly from it (the exact trap the
 * source prototypes' own code comments warn about). The modal system
 * (form/question/confirmation) is plain JS — it isn't scroll-driven.
 */
(function () {
  'use strict';

  var OPTION_LABELS = [
    'More consistent energy throughout the day',
    'Better sleep and recovery',
    'Improved focus and productivity',
    'Support for healthy ageing and longevity',
    'A simpler, more balanced health routine'
  ];

  /* Same order/index as OPTION_LABELS — sent as primaryMotivation. */
  var OPTION_API_VALUES = [
    'MORE_CONSISTENT_ENERGY',
    'BETTER_SLEEP_AND_RECOVERY',
    'IMPROVED_FOCUS_AND_PRODUCTIVITY',
    'HEALTHY_AGEING_AND_LONGEVITY',
    'SIMPLER_BALANCED_HEALTH_ROUTINE'
  ];

  /* Waitlist registration API (Decode Age interest-registrations, staging). */
  var API_ENDPOINT = 'https://api-ips.decodeage.in/integration/v1/interest-registrations';
  var API_INTEGRATION_SOURCE = 'SHOPIFY';
  var CONSENT_TEXT_VERSION = 'rise-rest-interest-v1';
  /* Both the request timeout and the minimum time the submit button stays
   * disabled after a click, so a fast failure still gives the user a beat
   * before they can hammer the button again, and a hung request doesn't
   * leave it disabled forever. */
  var SUBMIT_TIMEOUT_MS = 5000;

  var LINKEDIN_URL_RE = /^https:\/\/([\w-]+\.)?linkedin\.com\/in\/[A-Za-z0-9\-_%.]+\/?(\?.*)?$/i;

  var FIELD_ERROR_MESSAGES = {
    fullName: 'Please enter your name.',
    email: 'Please enter a valid email address.',
    mobile: 'Please enter a valid mobile number for the selected country.',
    dob: 'Please enter a valid date of birth (DD/MM/YYYY).',
    social: 'Please enter a valid LinkedIn profile URL (must start with https://).'
  };

  var NAME_INVALID_CHAR_MESSAGE = 'No special character allowed.';
  var NAME_LIMIT_REACHED_MESSAGE = 'Character limit reached.';

  /* Phone country-code support (window.RR_COUNTRY_CODES /
   * RR_COUNTRY_PHONE_LENGTHS come from assets/rr-country-codes.js, loaded
   * before this file — see the comment in rr-application-modal.liquid).
   * Free functions rather than methods so _fieldInvalid, _bindMobileField
   * and _submitApplication can all share one source of truth without
   * caring which one ran _bindPhoneCode first. */
  function countryByIso(iso) {
    var list = window.RR_COUNTRY_CODES || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].iso === iso) return list[i];
    }
    return null;
  }

  /* Countries in RR_COUNTRY_PHONE_LENGTHS get their exact national-number
   * length(s) enforced; every other country falls back to a generic
   * 4-14 digit range (E.164's national-number length ceiling, roughly). */
  function mobileMaxDigits(iso) {
    var lens = (window.RR_COUNTRY_PHONE_LENGTHS || {})[iso];
    return lens && lens.length ? Math.max.apply(null, lens) : 14;
  }

  function mobileDigitsValid(iso, digits) {
    var lens = (window.RR_COUNTRY_PHONE_LENGTHS || {})[iso];
    if (lens && lens.length) return lens.indexOf(digits.length) !== -1;
    return digits.length >= 4 && digits.length <= 14;
  }

  /* Merchant-configured allow-list for email endings (modal_email_allowed_
   * endings in rr-hero.liquid's schema, read here off data-email-allowed-
   * endings) — comma-separated, no leading dot (e.g. "com, in"). Blank/unset
   * allows any ending, so the restriction is opt-in from the theme editor.
   * Suffix match (not just the last segment) so compound endings like
   * "co.in" work the same as plain ones. */
  function parsedEmailEndings(el) {
    return (el.dataset.emailAllowedEndings || '')
      .split(',')
      .map(function (s) { return s.trim().replace(/^\.+/, '').toLowerCase(); })
      .filter(Boolean);
  }

  function emailEndingAllowed(el, val) {
    var endings = parsedEmailEndings(el);
    if (!endings.length) return true;
    var domain = val.split('@').pop().toLowerCase();
    return endings.some(function (ending) {
      return domain === ending || domain.slice(-(ending.length + 1)) === '.' + ending;
    });
  }

  /* Only called once emailEndingAllowed has already failed, so endings is
     never empty here (an empty allow-list means "allow any ending", which
     passes emailEndingAllowed above). */
  function emailEndingErrorMessage(el) {
    var endings = parsedEmailEndings(el);
    return 'Please use an email ending in ' + endings.map(function (e) { return '.' + e; }).join(', ') + '.';
  }

  var DOB_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

  /* Earliest date the calendar will offer, mirroring parseDob's own
   * `year < 1900` floor so the picker can't hand back a date the parser
   * would immediately reject. */
  var DOB_MIN_ISO = '1900-01-01';

  /* Client-local "today" as YYYY-MM-DD — deliberately computed at call time
   * (not baked in via Liquid) so it can't go stale behind any page cache,
   * and used both for the calendar companion's `max` (_bindDobField) and
   * here to reject future dates typed or picked on either path. */
  function todayISO() {
    var d = new Date();
    var month = d.getMonth() + 1, day = d.getDate();
    return d.getFullYear() + '-' + (month < 10 ? '0' + month : month) + '-' + (day < 10 ? '0' + day : day);
  }

  /* Converts a typed "DD/MM/YYYY" into "YYYY-MM-DD" for the API, or returns
   * null if it isn't a real calendar date, or is a future one. Kept as a
   * plain function (not a prototype method) since it has no dependency on
   * modal state. */
  function parseDob(value) {
    var m = DOB_RE.exec((value || '').trim());
    if (!m) return null;
    var day = +m[1], month = +m[2], year = +m[3];
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > new Date(year, month, 0).getDate()) return null;
    if (year < 1900) return null;
    var iso = year + '-' + (month < 10 ? '0' + month : month) + '-' + (day < 10 ? '0' + day : day);
    return iso > todayISO() ? null : iso;
  }

  /* Reads the dob field, which is a masked type="text" input on every
   * browser now (see _bindDobField) — so its value is always DD/MM/YYYY or
   * junk, and parseDob is the only path. The hidden companion date input
   * backing the calendar is never read here: picking a date writes through
   * to this field, so there's a single source of truth either way. */
  function dobToISO(el) {
    if (!el) return null;
    return parseDob(el.value);
  }

  /* Formats a raw digit stream as DD/MM/YYYY, dropping anything non-digit
   * the user typed or pasted. Length-capped at 8 digits so a fast typist
   * can't push past the year. */
  function maskDob(value) {
    var digits = (value || '').replace(/[^\d]/g, '').slice(0, 8);
    var parts = [];
    if (digits.length > 0) parts.push(digits.slice(0, 2));
    if (digits.length > 2) parts.push(digits.slice(2, 4));
    if (digits.length > 4) parts.push(digits.slice(4, 8));
    return parts.join('/');
  }

  function isDigitAt(str, i) {
    var c = str.charCodeAt(i);
    return c >= 48 && c <= 57;
  }

  /* The two halves of caret restoration (see the input handler in
   * _bindDobField). Position is tracked as a *digit* count rather than a
   * character offset, so it survives the mask inserting or dropping the
   * slashes around it: countDigitsBefore reads the count off the string the
   * user just edited, offsetAfterDigits finds the matching spot in the
   * reformatted one. */
  function countDigitsBefore(value, caret) {
    var n = 0;
    for (var i = 0; i < caret; i++) if (isDigitAt(value, i)) n++;
    return n;
  }

  function offsetAfterDigits(value, n) {
    if (n <= 0) return 0;
    var seen = 0;
    for (var i = 0; i < value.length; i++) {
      if (isDigitAt(value, i) && ++seen === n) return i + 1;
    }
    return value.length;
  }

  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function smooth(t) { return t * t * (3 - 2 * t); }

  var VALIDATION_ERROR_FIELDS = ['registrant.mobileNumber', 'registrant.email', 'registrant.linkedinProfileUrl'];

  /* True only for this exact API error shape: code VALIDATION_FAILED with a
   * details[] entry naming one of VALIDATION_ERROR_FIELDS — not any other
   * VALIDATION_FAILED (a different field could trip that same code for
   * reasons unrelated to these two), so this can't misfire onto an
   * unrelated validation error that happens to share the code. */
  function isFieldValidationError(data) {
    var err = data && data.error;
    if (!err || err.code !== 'VALIDATION_FAILED') return false;
    var details = err.details;
    if (!details || !details.length) return false;
    for (var i = 0; i < details.length; i++) {
      if (VALIDATION_ERROR_FIELDS.indexOf(details[i].field) !== -1) return true;
    }
    return false;
  }

  /* Format requested by Decode Age: "shopify-form-submission-<number>" —
   * the prefix stays fixed, only the number changes per session. */
  function generateId() {
    return 'shopify-form-submission-' + Date.now();
  }

  var SESSION_ID_KEY = 'rr_application_id';
  var SESSION_FINGERPRINT_KEY = 'rr_application_fingerprint';
  var sessionIdFallback = null;
  var sessionFingerprintFallback = null;

  /* Identifies WHO is being registered, not the full payload — this is
   * deliberately just email+mobile, since those are the two fields the API
   * itself treats as identifying a registrant (see the 409 duplicate-field
   * handling in _showSubmitError). */
  function registrantFingerprint(email, mobileNumber) {
    return (email || '').trim().toLowerCase() + '|' + (mobileNumber || '').trim();
  }

  /* Idempotency keys exist so the server can recognize a retried request
   * (failed fetch + user clicks "Apply" again, a network timeout, a
   * double-click, etc.) as the same attempt rather than a brand-new
   * registration — that only works if the key stays the same across those
   * retries. But "same attempt" only holds while it's the same registrant:
   * if the user submits, that fails, and they then fill the form again for
   * a DIFFERENT person in the same browser session, reusing the old key
   * would tell the server this is a replay of person A's request and risk
   * it never creating person B's registration at all. So the key is keyed
   * to (and rotates with) the email+mobile fingerprint, not just session
   * lifetime — same registrant as last attempt reuses the stored key, a
   * different one mints a fresh key. Success still clears both (see
   * clearSessionSubmissionId), since any submission after a success is
   * necessarily a new attempt regardless of fingerprint. */
  function getSessionSubmissionId(email, mobileNumber) {
    var fingerprint = registrantFingerprint(email, mobileNumber);
    try {
      var existingId = window.sessionStorage.getItem(SESSION_ID_KEY);
      var existingFingerprint = window.sessionStorage.getItem(SESSION_FINGERPRINT_KEY);
      if (existingId && existingFingerprint === fingerprint) return existingId;
      var fresh = generateId();
      window.sessionStorage.setItem(SESSION_ID_KEY, fresh);
      window.sessionStorage.setItem(SESSION_FINGERPRINT_KEY, fingerprint);
      return fresh;
    } catch (e) {
      // sessionStorage can throw in some private-browsing modes — fall back
      // to an in-memory value, still fingerprint-checked for the page's
      // lifetime at least.
      if (!sessionIdFallback || sessionFingerprintFallback !== fingerprint) {
        sessionIdFallback = generateId();
        sessionFingerprintFallback = fingerprint;
      }
      return sessionIdFallback;
    }
  }

  function clearSessionSubmissionId() {
    try {
      window.sessionStorage.removeItem(SESSION_ID_KEY);
      window.sessionStorage.removeItem(SESSION_FINGERPRINT_KEY);
    } catch (e) {}
    sessionIdFallback = null;
    sessionFingerprintFallback = null;
  }

  function RiseAndRest() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.topbar = document.querySelector('.rr-topbar');
    this.logoWhite = document.querySelector('[data-logo-white]');
    this.logoBlue = document.querySelector('[data-logo-blue]');
    this.headerMode = null;
    this.ritDark = false;

    this._bindCycle();
    this._bindModal();
    this._bindReveals();

    if (this.reducedMotion) {
      // Nothing will ever crossfade the Ritual's night variant or later
      // Protocol steps in, so show/arrange a static final state rather than
      // leave real copy permanently invisible or stacked illegibly.
      Array.prototype.slice.call(document.querySelectorAll('[data-rit-label-night], [data-rit-text-night], [data-sachet-rest]')).forEach(function (el) {
        el.style.opacity = '1';
        el.style.visibility = 'visible';
      });
      var pImgs0 = Array.prototype.slice.call(document.querySelectorAll('[data-pimg]'));
      var pTexts0 = Array.prototype.slice.call(document.querySelectorAll('[data-ptext]'));
      var pItemsD0 = Array.prototype.slice.call(document.querySelectorAll('[data-pitem]'));
      pImgs0.slice(1).forEach(function (el) { el.style.display = 'none'; });
      pTexts0.slice(1).forEach(function (el) { el.style.display = 'none'; });
      pItemsD0.forEach(function (el) { if (el.dataset.step !== '0') el.style.display = 'none'; });
      var firstDot0 = document.querySelector('[data-pdot]');
      if (firstDot0) firstDot0.setAttribute('data-active', '');
      return;
    }

    this._bindScroll();
  }

  RiseAndRest.prototype._setHeaderMode = function (mode) {
    if (mode === this.headerMode || !this.topbar) return;
    this.headerMode = mode;
    var light = mode === 'solid' || mode === 'day';
    this.topbar.style.color = light ? 'var(--rr-color-ink)' : '#fff';
    if (this.logoWhite && this.logoBlue) {
      this.logoWhite.style.opacity = light ? '0' : '1';
      this.logoBlue.style.opacity = light ? '1' : '0';
    }
  };

  /* --------------------------------------------------------------------
   * Hero headline word-cycle (not scroll-driven, plain JS is enough)
   * ------------------------------------------------------------------ */
  RiseAndRest.prototype._bindCycle = function () {
    var words = Array.prototype.slice.call(document.querySelectorAll('[data-cycle-word]'));
    if (words.length < 2 || this.reducedMotion) return;
    var ci = 0;
    words.forEach(function (w, i) { if (i !== 0) { w.style.opacity = '0'; w.style.transform = 'translateY(0.4em)'; } });
    this._cycleT = setInterval(function () {
      var cur = words[ci];
      ci = (ci + 1) % words.length;
      var nxt = words[ci];
      words.forEach(function (w) {
        if (w === cur || w === nxt) return;
        w.style.opacity = '0';
        w.style.transform = 'translateY(0.4em)';
      });
      cur.style.opacity = '0';
      cur.style.transform = 'translateY(-0.4em)';
      nxt.style.transform = 'translateY(0.4em)';
      requestAnimationFrame(function () { nxt.style.opacity = '1'; nxt.style.transform = 'translateY(0)'; });
    }, 2600);
  };

  /* --------------------------------------------------------------------
   * Entrance reveals — ported verbatim from both prototypes' own
   * componentDidMount(): plain CSS transitions, driven by an
   * IntersectionObserver. Deliberately NOT GSAP/ScrollTrigger, which an
   * earlier version of this file used and which broke the effect in two
   * separate ways:
   *
   *   1. `filter` never animated. The `blur` fx (the Early Access headline)
   *      needs blur(14px) → none. CSS interpolates that correctly — the spec
   *      treats `none` as the identity filter, i.e. blur(0) — but GSAP does
   *      not parse `'none'` as a filter list, so it snapped straight to
   *      unblurred on the first frame while opacity/transform still eased.
   *      Net effect: the headline's signature blur reveal simply never
   *      happened, only a plain fade.
   *   2. It only ever played once (`once: true`). Both prototypes instead
   *      re-arm an element when it leaves, so the entrance replays every
   *      time you come down into a section again.
   *
   * The re-hide is guarded on `boundingClientRect.top > 0` — only elements
   * that left *below* the viewport are re-armed, so anything scrolled off
   * the TOP stays revealed. That guard is what keeps this from reading as
   * "text randomly disappears" while still replaying on every re-entry.
   * ------------------------------------------------------------------ */
  RiseAndRest.prototype._bindReveals = function () {
    var reveals = Array.prototype.slice.call(document.querySelectorAll('[data-reveal]'));
    if (!reveals.length) return;

    if (this.reducedMotion) {
      reveals.forEach(function (el) { el.style.opacity = '1'; el.style.transform = 'none'; el.style.filter = 'none'; });
      return;
    }

    var EASE = 'cubic-bezier(0.22,1,0.36,1)';
    var HIDDEN = {
      up: 'translateY(44px)',
      left: 'translateX(-64px)',
      right: 'translateX(64px)',
      zoom: 'scale(.88) translateY(20px)',
      pop: 'scaleX(.15)',
      blur: 'translateY(28px) scale(.985)'
    };

    function hide(el) {
      var fx = el.dataset.fx || 'up';
      el.style.transition = 'none';
      el.style.transitionDelay = '0ms';
      el.style.opacity = '0';
      el.style.transform = HIDDEN[fx] || HIDDEN.up;
      if (fx === 'blur') el.style.filter = 'blur(14px)';
      // Force a synchronous reflow so the hidden state is committed as its
      // own frame before the transition is re-attached — without this the
      // browser coalesces hide+reveal into one style recalc and there is
      // nothing to animate from.
      void el.offsetWidth;
      var dur = fx === 'pop' ? '.7s' : '.9s';
      el.style.transition = 'opacity ' + dur + ' ' + EASE + ', transform ' + dur + ' ' + EASE + ', filter .9s ease';
    }

    function reveal(el) {
      el.style.transitionDelay = (el.dataset.delay || '0') + 'ms';
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.style.filter = 'none';
    }

    // Only pre-hide what starts well below the fold, so above-the-fold copy
    // is never hidden-then-revealed (which would read as a flash on load).
    var vh0 = window.innerHeight || 800;
    reveals.forEach(function (el) {
      if (el.getBoundingClientRect().top > vh0 * 0.8) hide(el);
    });

    if (!window.IntersectionObserver) {
      reveals.forEach(reveal);
      return;
    }

    this._revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) reveal(en.target);
        else if (en.boundingClientRect.top > 0) hide(en.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    var io = this._revealIO;
    reveals.forEach(function (el) { io.observe(el); });
  };

  /* --------------------------------------------------------------------
   * Scroll-driven master controller — ported from the prototypes' own
   * `_layout()`/`_update()`. `position:sticky` (see rise-and-rest.css) does
   * all the pinning/overlap; this only drives the Ritual/Protocol internal
   * crossfades, the cover-darken/round-corner touch between sections, and
   * header colour mode. Runs identically on mobile and desktop — neither
   * the pin/overlap mechanic nor this math branches on viewport width
   * (only the CSS *layout* of each section's own content does).
   * ------------------------------------------------------------------ */
  RiseAndRest.prototype._bindScroll = function () {
    var self = this;

    this.heroEl = document.querySelector('[data-hero]');
    this.ritualEl = document.querySelector('[data-ritual]');
    this.protoEl = document.querySelector('[data-protocol]');
    this.finalEl = document.querySelector('[data-final]');
    this.ritAnchor = document.querySelector('[data-rit-anchor]');
    this.protoAnchor = document.querySelector('[data-proto-anchor]');
    this.finalAnchor = document.querySelector('[data-final-anchor]');

    /* Theme-editor setting (sections/rr-hero.liquid), read off the Hero
       section's own data attribute rather than a global — Hero is always
       the first section on the page, and owns the other page-wide
       settings (video/waves/text-bg) the same way. */
    this.scrollStyle = (this.heroEl && this.heroEl.dataset.scrollStyle) || 'scrub';
    this.logoMode = (this.heroEl && this.heroEl.dataset.logoMode) || 'auto';
    this.heroScrollZoom = !(this.heroEl && this.heroEl.dataset.scrollZoom === 'false');

    this.ritNight = document.querySelector('[data-rit-night]');
    this.ritSun = document.querySelector('[data-rit-sun]');
    this.ritMoon = document.querySelector('[data-rit-moon]');
    this.wordDay = document.querySelector('[data-rit-word-day]');
    this.wordNight = document.querySelector('[data-rit-word-night]');
    this.sachetRise = document.querySelector('[data-sachet-rise]');
    this.sachetRest = document.querySelector('[data-sachet-rest]');
    this.labelDay = document.querySelector('[data-rit-label-day]');
    this.labelNight = document.querySelector('[data-rit-label-night]');
    this.textDay = document.querySelector('[data-rit-text-day]');
    this.textNight = document.querySelector('[data-rit-text-night]');
    this.ritKicker = document.querySelector('[data-rit-kicker]');
    this.ritNote = document.querySelector('[data-rit-note]');
    this.stageEl = document.querySelector('[data-bottle-stage]');
    if (this.textNight) this.textNight.style.visibility = 'visible';
    if (this.labelNight) this.labelNight.style.visibility = 'visible';

    // Mobile band items (data-pimg/data-ptext) and desktop 2-column items
    // (data-pitem) are two entirely separate DOM sets — never both on the
    // same element — so each breakpoint's branch below only ever touches
    // its own elements, with nothing to hide/reset from the other.
    this.pImgs = Array.prototype.slice.call(document.querySelectorAll('[data-pimg]'));
    this.pTexts = Array.prototype.slice.call(document.querySelectorAll('[data-ptext]'));
    this.pItemsD = Array.prototype.slice.call(document.querySelectorAll('[data-pitem]'));
    this.pDots = Array.prototype.slice.call(document.querySelectorAll('[data-pdot]'));
    this.pSteps = Math.max(this.pImgs.length, this.pTexts.length, 1);

    this._layout();
    this._update();

    var raf = 0;
    var onScroll = function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; self._update(); });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { self._layout(); self._update(); });
    window.addEventListener('load', function () { self._layout(); self._update(); });
    try {
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self._layout(); self._update(); });
    } catch (e) {}

    if (this.scrollStyle === 'snap') this._bindSnapScroll();
  };

  /* --------------------------------------------------------------------
   * "Snap on scroll" (theme editor: Hero → Scroll transitions). Opt-in
   * alternative to the default scroll-scrubbed handoffs — a small
   * wheel/touch nudge while inside one of the three section-to-section
   * cover-transition windows (hero→ritual, ritual→protocol,
   * protocol→final) auto-completes that transition instead of requiring
   * the user to manually scroll through it.
   *
   * Deliberately scoped to ONLY the cover-slide windows, never each
   * section's own internal scroll animation (Ritual's day/night crossfade,
   * Protocol's 4-step carousel) — those stay fully manual regardless of
   * this setting. A window is exactly the last 1 viewport-height of
   * scroll before a `*DwellStart` value (see _layout): `*DwellStart`
   * itself already marks the scrollY where the next section finishes
   * sliding up and fully covers the previous one (ported from the same
   * math the cover-transition dim/scale effect uses), so the window is
   * simply [dwellStart - vh, dwellStart).
   *
   * Never bound at all when reducedMotion is set — RiseAndRest's
   * constructor returns before calling _bindScroll entirely in that case,
   * so there's nothing extra to guard here.
   * ------------------------------------------------------------------ */
  RiseAndRest.prototype._bindSnapScroll = function () {
    var self = this;
    this.snapping = false;

    function zones() {
      return [self.ritDwellStart, self.protoDwellStart, self.finalDwellStart];
    }

    function activeZoneEnd(sy) {
      var list = zones();
      for (var i = 0; i < list.length; i++) {
        var end = list[i];
        if (!end) continue;
        if (sy >= end - self.vh && sy < end) return end;
      }
      return null;
    }

    function cancelSnap() {
      if (self._snapRaf) cancelAnimationFrame(self._snapRaf);
      self._snapRaf = 0;
      self.snapping = false;
      clearTimeout(self._snapFailsafe);
    }

    function snapTo(target) {
      cancelSnap();
      self.snapping = true;
      var startY = window.scrollY;
      var dist = target - startY;
      var duration = 650;
      var t0 = 0;
      /* Failsafe. `snapping` gates a preventDefault() on every wheel event,
         so if it is ever left stuck true, scrolling is permanently dead from
         the user's point of view. The rAF loop below can legitimately stop
         running — a backgrounded tab pauses rAF entirely — and would
         otherwise never reach the branch that clears the flag. This
         guarantees it is always released. */
      clearTimeout(self._snapFailsafe);
      self._snapFailsafe = setTimeout(function () {
        if (self.snapping) { self.snapping = false; self._snapRaf = 0; absorbing = false; }
      }, duration + 1200);
      // Driven off rAF's own DOMHighResTimeStamp rather than Date.now():
      // it is the timestamp of the frame being rendered, so the easing
      // advances in lockstep with the frames it is drawn on. Date.now() is
      // millisecond-resolution and sampled at callback time, which drifts
      // against the actual frame cadence and shows up as uneven motion.
      function step(now) {
        if (!t0) t0 = now;
        var t = clamp01((now - t0) / duration);
        window.scrollTo(0, startY + dist * smooth(t));
        // Update the cover transition in the SAME frame as the scroll it
        // belongs to. Relying on the scroll listener instead would land it
        // a frame late every frame (scrollTo → queued scroll event →
        // another rAF → _update), so the incoming section's scale/dim
        // visibly trailed the scroll position for the whole snap.
        // Never let a throw here kill the loop — it would strand `snapping`
        // true (see the failsafe above) and freeze scrolling outright.
        try { self._update(); } catch (err) {}
        if (t < 1) {
          self._snapRaf = requestAnimationFrame(step);
        } else {
          self._snapRaf = 0;
          self.snapping = false;
          clearTimeout(self._snapFailsafe);
          // Keep swallowing the rest of THIS gesture. A trackpad flick emits
          // momentum for well over a second — longer than the snap itself —
          // and releasing it the instant the snap lands would let the
          // leftover momentum immediately scroll the user straight back out
          // of the section they were just snapped to.
          absorbing = true;
        }
      }
      self._snapRaf = requestAnimationFrame(step);
    }

    /* Wheel-specific guards.
       Touch already has a natural one-shot boundary: a snap nulls
       touchStartY, so nothing further can fire until the user lifts and
       touches again. A wheel has no such boundary. One trackpad flick emits
       a continuous momentum stream that (a) outlives the 650ms snap and
       (b) during decay contains small opposite-sign deltas. Against the
       previous logic that stream could cancel the very snap it had just
       started (any deltaY < 0 cancelled), then immediately start another,
       leaving wheel input thrashing between states — which is exactly why
       this shows up on desktop while touch scrolling stays correct.
       `absorbing` gives the wheel the same one-gesture boundary touch has,
       and the cancel threshold ignores decay jitter while still honouring a
       real attempt to scroll back up. */
    var WHEEL_QUIET_MS = 180;    // gap that marks the end of one gesture
    var UP_CANCEL_DELTA = -24;   // genuine upward intent, not momentum jitter
    var lastWheelAt = 0;
    var absorbing = false;

    function onWheel(e) {
      var now = Date.now();
      var newGesture = (now - lastWheelAt) > WHEEL_QUIET_MS;
      lastWheelAt = now;
      if (newGesture) absorbing = false;

      if (self.snapping) {
        // Scrolling meaningfully back UP is "let me out" — cancel and hand
        // control straight back to native scrolling. Same-direction events
        // are swallowed so native scroll cannot fight the programmatic
        // scrollTo for the same pixels.
        if (e.deltaY <= UP_CANCEL_DELTA) { cancelSnap(); absorbing = false; return; }
        e.preventDefault();
        return;
      }
      if (absorbing) {
        // Tail of the gesture that just snapped: swallow it, but never
        // block a deliberate move back up.
        if (e.deltaY > 0) e.preventDefault();
        else absorbing = false;
        return;
      }
      if (e.deltaY <= 0) return;
      var target = activeZoneEnd(window.scrollY);
      if (target === null) return;
      e.preventDefault();
      snapTo(target);
    }

    var touchStartY = null;
    window.addEventListener('touchstart', function (e) {
      touchStartY = e.touches[0].clientY;
      // A deliberate new touch during a snap takes over immediately.
      if (self.snapping) cancelSnap();
    }, { passive: true });

    function onTouchMove(e) {
      if (self.snapping) { e.preventDefault(); return; }
      if (touchStartY === null) return;
      var dy = touchStartY - e.touches[0].clientY;
      if (dy < 12) return;
      var target = activeZoneEnd(window.scrollY);
      if (target === null) return;
      touchStartY = null;
      e.preventDefault();
      snapTo(target);
    }

    /* These two listeners have to be non-passive, because snapping means
       calling preventDefault() on the input that triggered it. But a
       non-passive wheel/touchmove listener on `window` is scroll-blocking:
       the compositor cannot scroll until the main thread has run the
       handler and reported back whether it cancelled the event. Registered
       for the whole page (as they previously were), that permanently opts
       the entire document out of compositor-driven scrolling, so every
       unrelated main-thread hiccup — the hero's playing video, or this
       store's third-party tags — turns into visible scroll stutter, even
       nowhere near a snap zone.

       So they are attached only while the scroll position is actually
       inside one of the three snap windows (~1vh each), and removed again
       on the way out. Everywhere else the page scrolls straight off the
       compositor as it would with the setting turned off entirely. The
       zone check itself rides a passive scroll listener, which is not
       scroll-blocking. */
    var blockingBound = false;
    function syncBlockingListeners() {
      var inZone = activeZoneEnd(window.scrollY) !== null;
      if (inZone && !blockingBound) {
        window.addEventListener('wheel', onWheel, { passive: false });
        window.addEventListener('touchmove', onTouchMove, { passive: false });
        blockingBound = true;
      } else if (!inZone && blockingBound && !self.snapping) {
        window.removeEventListener('wheel', onWheel, { passive: false });
        window.removeEventListener('touchmove', onTouchMove, { passive: false });
        blockingBound = false;
      }
    }
    window.addEventListener('scroll', syncBlockingListeners, { passive: true });
    window.addEventListener('resize', syncBlockingListeners);
    syncBlockingListeners();

    // Any explicit keyboard scroll intent aborts an in-flight snap, so the
    // page can never feel stuck to a keyboard or assistive-tech user.
    window.addEventListener('keydown', function (e) {
      if (!self.snapping) return;
      if (/^(Arrow|Page|Home|End| )/.test(e.key) || e.key === 'Escape') cancelSnap();
    });

    /* Only ever rendered as a real <button> (see sections/rr-hero.liquid)
       when this "snap" setting is selected in the first place — the
       "scrub" mode markup is a plain non-interactive <span> with no
       data-scroll-cue attribute, so there's nothing here to bind to and
       no extra mode-check needed. */
    var scrollCue = document.querySelector('[data-scroll-cue]');
    if (scrollCue && self.ritDwellStart) {
      scrollCue.addEventListener('click', function () {
        if (self.snapping) return;
        snapTo(self.ritDwellStart);
      });
    }
  };

  /* Cache the document-Y each pinned section's dwell phase starts at, read
   * off the plain (non-sticky) anchor spacer that precedes it — never off
   * the sticky section itself (see the file-header comment for why). */
  RiseAndRest.prototype._layout = function () {
    this.vh = window.innerHeight || 800;
    // Absolute document-Y where each next section *effectively* starts
    // (i.e. the scroll position at which it finishes sliding up and fully
    // covers the previous section). The anchor's own raw bottom edge is
    // one full viewport height short of that now: rise-and-rest.css gives
    // the anchor an extra 100vh (so the outgoing section's native sticky
    // range natively covers the whole slide, see the comment there) and
    // pulls the next section back up by that same 100vh via a negative
    // margin, so subtract it back out here to land on the section's real
    // effective start. Reading off the anchor (never sticky/margined)
    // rather than the section itself keeps this constant regardless of
    // current scrollY.
    this.ritDwellStart = this.ritAnchor
      ? this.ritAnchor.getBoundingClientRect().bottom + window.scrollY - this.vh
      : 0;
    this.protoDwellStart = this.protoAnchor
      ? this.protoAnchor.getBoundingClientRect().bottom + window.scrollY - this.vh
      : 0;
    this.finalDwellStart = this.finalAnchor
      ? this.finalAnchor.getBoundingClientRect().bottom + window.scrollY - this.vh
      : 0;

    // Mobile only: the sachet stage's CSS band (top/bottom) is a static
    // clamp() fallback sized for the default copy. Center it in the real
    // gap between the "Morning/rise" label and the bottom headline instead,
    // measured live, so it still fits if custom fonts/copy change how much
    // vertical space the label and headline actually take up. Desktop never
    // does this — its sachet is a fixed right-side column, not a band.
    if (window.innerWidth < 761 && this.stageEl && this.ritualEl && this.labelDay && this.textDay) {
      var secTop = this.ritualEl.getBoundingClientRect().top;
      var secH = this.ritualEl.clientHeight || this.vh;
      var labelBottom = this.labelDay.getBoundingClientRect().bottom - secTop;
      var textTop = this.textDay.getBoundingClientRect().top - secTop;
      if (textTop - labelBottom > 80) {
        this.stageEl.style.top = (labelBottom + 18) + 'px';
        this.stageEl.style.bottom = (secH - textTop + 18) + 'px';
      }
    } else if (this.stageEl) {
      this.stageEl.style.top = '';
      this.stageEl.style.bottom = '';
    }
  };

  RiseAndRest.prototype._update = function () {
    var vh = this.vh || window.innerHeight || 800;
    var sy = window.scrollY || 0;

    /* ---- Ritual: day → night crossfade (ported verbatim from _update()) ---- */
    if (this.ritualEl) {
      var rp = (sy - this.ritDwellStart) / vh;
      var tT = smooth(clamp01((rp - 0.15) / 0.6));
      if (this.sachetRise) { this.sachetRise.style.opacity = (1 - tT).toFixed(3); this.sachetRise.style.transform = 'translateY(' + (-tT * 40).toFixed(1) + 'px) scale(' + (1 - tT * 0.05).toFixed(3) + ')'; }
      if (this.sachetRest) { this.sachetRest.style.opacity = tT.toFixed(3); this.sachetRest.style.transform = 'translateY(' + ((1 - tT) * 40).toFixed(1) + 'px) scale(' + (0.95 + tT * 0.05).toFixed(3) + ')'; }
      if (this.ritNight) this.ritNight.style.opacity = tT.toFixed(3);
      if (this.ritSun) { this.ritSun.style.opacity = (1 - tT).toFixed(3); this.ritSun.style.transform = 'translateY(' + (tT * 26).toFixed(2) + 'vh)'; }
      if (this.ritMoon) { this.ritMoon.style.opacity = tT.toFixed(3); this.ritMoon.style.transform = 'translateY(' + ((1 - tT) * 16).toFixed(2) + 'vh)'; }
      if (this.wordDay) { this.wordDay.style.opacity = (1 - tT).toFixed(3); this.wordDay.style.transform = 'translateX(' + (clamp01(rp) * -36).toFixed(1) + 'px)'; }
      if (this.wordNight) { this.wordNight.style.opacity = tT.toFixed(3); this.wordNight.style.transform = 'translateX(' + (((1 - tT) * 70) + Math.max(0, rp - 1.65) * -30).toFixed(1) + 'px)'; }
      if (this.labelDay) { this.labelDay.style.opacity = (1 - tT).toFixed(3); this.labelDay.style.transform = 'translateY(' + (tT * -10).toFixed(1) + 'px)'; }
      if (this.labelNight) { this.labelNight.style.opacity = tT.toFixed(3); this.labelNight.style.transform = 'translateY(' + ((1 - tT) * 10).toFixed(1) + 'px)'; }
      if (this.textDay) { this.textDay.style.opacity = (1 - tT).toFixed(3); this.textDay.style.visibility = tT > 0.98 ? 'hidden' : 'visible'; }
      if (this.textNight) { this.textNight.style.opacity = tT.toFixed(3); this.textNight.style.visibility = tT < 0.02 ? 'hidden' : 'visible'; }
      var dark = tT > 0.5;
      if (dark !== this.ritDark) {
        this.ritDark = dark;
        if (this.ritKicker) this.ritKicker.style.color = dark ? 'rgba(255,255,255,0.78)' : 'rgba(38,22,6,0.68)';
        if (this.ritNote) this.ritNote.style.color = dark ? 'rgba(255,255,255,0.35)' : 'rgba(20,10,2,0.35)';
      }
    }

    /* ---- Protocol: 4-step carousel. Mobile and desktop use genuinely
       different motion in the real prototypes (mobile: a horizontal image
       band / text band slide; desktop: a vertical crossfade inside a
       2-column grid) — ported verbatim from each prototype's own _update(),
       branched the same way the CSS layout already branches (761px). ---- */
    if (this.protoEl && this.pSteps) {
      var pp = (sy - this.protoDwellStart) / (vh * 3.4);
      if (window.innerWidth < 761) {
        // Mobile: image band exits right / re-enters from the left, text
        // band exits left / re-enters from the right.
        var f1 = smooth(clamp01((pp - 0.08) / 0.18));
        var f2 = smooth(clamp01((pp - 0.31) / 0.18));
        var f3 = smooth(clamp01((pp - 0.56) / 0.18));
        var IN = [1, f1, f2, f3];
        var OUT = [f1, f2, f3, 0];
        var OFF = 122;
        var placeMobile = function (el, sign) {
          var st = +el.dataset.step;
          var inn = IN[st], out = OUT[st];
          var x = sign * (-OFF * (1 - inn) + OFF * out);
          el.style.transform = 'translate3d(' + x.toFixed(2) + '%,0,0)';
          el.style.visibility = 'visible';
          var op = inn * (1 - out);
          el.style.opacity = op.toFixed(3);
          el.style.pointerEvents = op > 0.6 ? 'auto' : 'none';
        };
        this.pImgs.forEach(function (el) { placeMobile(el, 1); });
        this.pTexts.forEach(function (el) { placeMobile(el, -1); });
        var activeM = f3 > 0.5 ? 3 : f2 > 0.5 ? 2 : f1 > 0.5 ? 1 : 0;
        this.pDots.forEach(function (d, i) {
          if (i === activeM) d.setAttribute('data-active', '');
          else d.removeAttribute('data-active');
        });
      } else {
        // Desktop: vertical crossfade — text exits up, image exits down.
        // No opacity fade here (unlike mobile) — the prototype relies
        // purely on translateY + the pcell's overflow:hidden to hide it.
        var t1 = smooth(clamp01((pp - 0.10) / 0.22));
        var t2 = smooth(clamp01((pp - 0.40) / 0.22));
        var t3 = smooth(clamp01((pp - 0.70) / 0.22));
        var T = [0, t1, t2, t3];
        var placeDesktop = function (el) {
          var s = +el.dataset.step;
          var kind = el.dataset.kind;
          var inn = s === 0 ? 1 : T[s];
          var out = s === 3 ? 0 : T[s + 1];
          var y = kind === 'text' ? (112 * (1 - inn) - 112 * out) : (-112 * (1 - inn) + 112 * out);
          el.style.transform = 'translateY(' + y.toFixed(2) + '%)';
          el.style.visibility = (inn <= 0.001 || out >= 0.999) ? 'hidden' : 'visible';
          el.style.opacity = '';
          el.style.pointerEvents = '';
        };
        this.pItemsD.forEach(placeDesktop);
        var activeD = t3 > 0.5 ? 3 : t2 > 0.5 ? 2 : t1 > 0.5 ? 1 : 0;
        this.pDots.forEach(function (d, i) {
          if (i === activeD) d.setAttribute('data-active', '');
          else d.removeAttribute('data-active');
        });
      }
    }

    /* ---- Cover transitions: next pinned section slides over the previous
       one (scale-down + darken outgoing, round the incoming corners).
       Holding the outgoing section still during the slide is no longer
       done here — it's now a zero-JS side effect of the extended-anchor +
       negative-margin CSS (see the long comment above `.rr-anchor` in
       rise-and-rest.css); this loop only owns the decorative dim/scale and
       corner-round touch. (Two earlier approaches tried to hold position
       from JS instead — a `position:fixed` swap, then a `translateY`
       counter-offset — both since removed: the CSS fix is native and has
       no per-frame timing race against the browser's own sticky release,
       which is what caused the flicker the `translateY` version had.) ---- */
    /* All three rects are read up front, BEFORE any style write in this
       block, and the header-mode check below then reuses them instead of
       re-reading. Interleaving reads and writes (the previous shape: read
       rect → write transform/filter/radius → read next rect → …) forces the
       browser to flush layout on every read, so this loop alone cost ~4-6
       synchronous reflows per frame — enough to blow the frame budget
       during a programmatic snap, on top of the dozens of inline styles the
       Ritual/Protocol blocks above already wrote. Batched this way it costs
       exactly one. */
    var coverTops = [
      this.ritualEl ? this.ritualEl.getBoundingClientRect().top : null,
      this.protoEl ? this.protoEl.getBoundingClientRect().top : null,
      this.finalEl ? this.finalEl.getBoundingClientRect().top : null
    ];

    var pairs = [[this.heroEl, this.ritualEl], [this.ritualEl, this.protoEl], [this.protoEl, this.finalEl]];
    var cache = this._coverCache || (this._coverCache = []);
    // Hero-only opt-out (rr-hero.liquid's hero_scroll_zoom setting): the
    // hero's video fills it edge-to-edge against .rr-scope's near-black
    // background, so shrinking it during the cover transition below reveals
    // a thin dark edge around the video. Skipping just pair 0's `a` writes
    // leaves Ritual's incoming corner-round (this loop's `b`) untouched.
    var heroScrollZoom = this.heroScrollZoom;
    pairs.forEach(function (pair, i) {
      var a = pair[0], b = pair[1];
      if (!a || !b || coverTops[i] === null) return;
      var skipHeroZoom = i === 0 && !heroScrollZoom;
      var c = clamp01(1 - coverTops[i] / vh);
      // Quantised to the precision actually rendered below, so a section
      // sitting at a steady state (c === 0 or 1, i.e. most of the page most
      // of the time) stops re-writing identical transform/filter strings
      // every frame. Writing `filter` on a viewport-sized section forces a
      // repaint even when the value is unchanged.
      var q = Math.round(c * 10000);
      if (cache[i] === q) return;
      cache[i] = q;
      if (c === 0) {
        // Fully idle: clear the properties outright rather than writing the
        // identity values scale(1)/brightness(1). An identity `filter` is
        // still a filter — it forces the section onto its own composited
        // layer and makes the compositor re-filter it on every paint. For
        // .rr-hero that means re-filtering an autoplaying video ~30-60
        // times a second for the whole time the hero is on screen, which is
        // continuous GPU work for a visual no-op. `will-change` is dropped
        // with them so the layer is not pinned in memory while idle either.
        a.style.transform = '';
        a.style.filter = '';
        a.style.willChange = '';
        b.style.borderTopLeftRadius = '';
        b.style.borderTopRightRadius = '';
        return;
      }
      // Promoted only while actually mid-transition. At c === 1 the section
      // is completely covered by the next one, so it still needs its
      // transform/filter to be correct (the user can scroll back up into
      // it) but there is nothing animating left to promote for.
      if (!skipHeroZoom) {
        a.style.willChange = c < 1 ? 'transform, filter' : '';
        a.style.transform = 'scale(' + (1 - 0.06 * c).toFixed(4) + ')';
        a.style.filter = 'brightness(' + (1 - 0.4 * c).toFixed(3) + ')';
      }
      var r = c < 1 ? 30 * (1 - c) : 0;
      b.style.borderTopLeftRadius = r.toFixed(1) + 'px';
      b.style.borderTopRightRadius = r.toFixed(1) + 'px';
    });

    /* ---- Header colour mode: day over hero/ritual-day, night over
       ritual-night, solid over protocol, day again over early access.
       Reuses the rects read above — re-reading here would reintroduce a
       forced reflow right after the writes in the loop. ---- */
    var rTop = coverTops[0] === null ? 1e9 : coverTops[0];
    var pTop = coverTops[1] === null ? 1e9 : coverTops[1];
    var fTop = coverTops[2] === null ? 1e9 : coverTops[2];
    var mode = 'day';
    if (pTop < 70 && fTop > 70) mode = 'solid';
    else if (rTop < 70 && pTop >= 70) mode = this.ritDark ? 'night' : 'day';
    // Section setting: forces which logo variant shows while still purely
    // over the Hero (Ritual hasn't started covering it yet). Once the user
    // scrolls past that point the normal crossfade above takes back over.
    if (rTop >= 70 && this.logoMode !== 'auto') {
      mode = this.logoMode === 'white' ? 'night' : 'day';
    }
    this._setHeaderMode(mode);
  };

  /* --------------------------------------------------------------------
   * Application modal: open/close/splash, validation, step flow, a11y
   * ------------------------------------------------------------------ */
  RiseAndRest.prototype._bindModal = function () {
    var self = this;
    this.modal = document.querySelector('[data-modal]');
    this.splash = document.querySelector('[data-splash]');
    if (!this.modal) return;

    this.form = this.modal.querySelector('[data-rr-form]');
    this._bindDobField();
    this._bindPhoneCode();
    this._bindMobileField();
    this._bindNameField();
    this.stepForm = this.modal.querySelector('[data-step-form]');
    this.stepQuestion = this.modal.querySelector('[data-step-question]');
    this.stepDone = this.modal.querySelector('[data-step-done]');
    this.liveRegion = this.modal.querySelector('[data-modal-live]');
    this.modalBanner = this.modal.querySelector('.rr-modal__banner');
    this.modalUnderline = this.modal.querySelector('.rr-modal__underline');
    this.bannerCopy = this.modal.querySelector('.rr-modal__banner-copy');
    this.bannerPerks = this.modal.querySelector('.rr-modal__perks');
    this.audience = 'me';
    this.lastTrigger = null;

    var openTriggers = Array.prototype.slice.call(document.querySelectorAll('[data-rr-open-modal]'));
    openTriggers.forEach(function (btn) {
      btn.addEventListener('click', function (e) { self.lastTrigger = btn; self.openModal(e); });
    });

    var closeEls = Array.prototype.slice.call(this.modal.querySelectorAll('[data-modal-close]'));
    closeEls.forEach(function (el) { el.addEventListener('click', function () { self.closeModal(); }); });

    var homeBtn = this.modal.querySelector('[data-go-home]');
    if (homeBtn) homeBtn.addEventListener('click', function () { self.closeModal(); window.scrollTo({ top: 0, behavior: 'smooth' }); });

    var backBtn = this.modal.querySelector('[data-step-back]');
    if (backBtn) backBtn.addEventListener('click', function () { self._goToStep('form'); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.modal.hasAttribute('data-open')) self.closeModal();
      if (e.key === 'Tab' && self.modal.hasAttribute('data-open')) self._trapFocus(e);
    });

    if (this.form) {
      this.form.addEventListener('submit', function (e) { self.onSubmit(e); });
      this.form.addEventListener('focusin', function (e) { if (e.target.matches('input,select')) e.target.style.borderBottomColor = 'var(--rr-da-brand-blue)'; });
      this.form.addEventListener('focusout', function (e) {
        if (e.target.matches('input,select')) e.target.style.borderBottomColor = 'var(--rr-color-border-default)';
        if (e.target.matches('#rr-email, #rr-mobile')) self._validateField(e.target);
      });
    }

    var pickMe = this.modal.querySelector('[data-pick-me]');
    var pickOther = this.modal.querySelector('[data-pick-other]');
    if (pickMe) pickMe.addEventListener('click', function () { self._setAudience('me'); });
    if (pickOther) pickOther.addEventListener('click', function () { self._setAudience('other'); });

    this._renderOptions();

    this.submitAnswerBtn = this.modal.querySelector('[data-submit-answer]');
    this.submitError = this.modal.querySelector('[data-submit-error]');
    // Captured once so the 409 duplicate-field message (set at submit time)
    // can be reverted to this on a later, differently-failing attempt,
    // without hardcoding a second copy of the copy from rr-application-modal.liquid.
    this.defaultSubmitErrorText = this.submitError ? this.submitError.textContent : '';
    // Step 1's own submit (Continue → _submitProfile) and its error banner —
    // separate from submitAnswerBtn/submitError above, which belong to step
    // 2's Apply button.
    this.continueBtn = this.modal.querySelector('[data-form-submit]');
    this.formSubmitError = this.modal.querySelector('[data-form-submit-error]');
    this.defaultFormSubmitErrorText = this.formSubmitError ? this.formSubmitError.textContent : '';
    // A prior 409 (duplicate email/mobile) leaves this.submitError visible;
    // editing either offending field should clear the stale banner rather
    // than waiting for another submit attempt (mirrors consentError/
    // questionError already being cleared as soon as the user acts on them).
    if (this.form) {
      this.form.addEventListener('input', function (e) {
        if (e.target.matches('#rr-email, #rr-mobile') && self.submitError && !self.submitError.hidden) {
          self.submitError.hidden = true;
        }
        if (e.target.matches('#rr-email, #rr-mobile') && self.formSubmitError && !self.formSubmitError.hidden) {
          self.formSubmitError.hidden = true;
        }
        /* A field's own inline error (set by onSubmit or blur) otherwise
         * only gets re-checked on the next submit — so correcting a value
         * left the stale message on screen until then. Only ever clears an
         * error that's already showing; never sets one, so this can't cause
         * errors to flash up mid-typing. */
        /* #rr-full-name is excluded — _bindNameField owns its own error
         * entirely (see the comment there for why sharing this listener
         * with it doesn't work). */
        var el = e.target;
        if (
          el.classList &&
          el.classList.contains('rr-field--error') &&
          el.matches('#rr-email, #rr-mobile, #rr-dob, #rr-social') &&
          !self._fieldInvalid(el)
        ) {
          self._setFieldError(el, '');
        }
      });
    }
    this.termsCheckbox = this.modal.querySelector('[name="terms2"]');
    this.consentError = this.modal.querySelector('[data-consent-error]');
    this.questionError = this.modal.querySelector('[data-question-error]');
    if (this.submitAnswerBtn) this.submitAnswerBtn.addEventListener('click', function () { self._onApply(); });
    if (this.termsCheckbox) {
      this.termsCheckbox.addEventListener('change', function () {
        if (self.termsCheckbox.checked && self.consentError) self.consentError.hidden = true;
      });
    }
  };

  RiseAndRest.prototype._onApply = function () {
    var optionSelected = this.selectedOption != null;
    var termsChecked = !this.termsCheckbox || this.termsCheckbox.checked;
    if (this.questionError) this.questionError.hidden = optionSelected;
    if (this.consentError) this.consentError.hidden = termsChecked;
    if (!optionSelected) {
      var firstOption = this.modal.querySelector('[data-question-options] .rr-question__option');
      if (firstOption) firstOption.focus();
      return;
    }
    if (!termsChecked) {
      this.termsCheckbox.focus();
      return;
    }
    this._submitApplication();
  };

  /* 409 responses carry error.details: [{ field, message }] for whichever
   * registrant fields collided with an existing registration — in practice
   * email and/or mobileNumber. Any other status, a body that fails to
   * parse, or a 409 whose details don't name either field all fall back to
   * the same static copy already in rr-application-modal.liquid. */
 RiseAndRest.prototype._showSubmitError = function (data) {
    if (!this.submitError) return;
    var err = data && data.error;
    var messages = [];
    var details = err && err.details;
    if (details && details.length) {
      for (var i = 0; i < details.length; i++) {
        var field = (details[i].field || '').toLowerCase();
        var isEmailOrMobile = field.indexOf('email') !== -1 || field.indexOf('mobile') !== -1 || field.indexOf('phone') !== -1;
        if (isEmailOrMobile && details[i].message) messages.push(details[i].message);
      }
    }
    // Real staging shape for this endpoint has no details[] — just a flat
    // error.code/message (e.g. ACTIVE_INTEREST_REGISTRATION_EXISTS). This
    // is only reached from the res.status === 409 branch, so a flat
    // error.message here is reliably the duplicate-registrant text.
    if (!messages.length && err && err.message) messages.push(err.message);

    this.submitError.textContent = messages.length ? messages.join(' ') : this.defaultSubmitErrorText;
    this.submitError.hidden = false;
};
  RiseAndRest.prototype._submitApplication = function () {
    var self = this;
    if (!this.form) return;
    var fd = new FormData(this.form);
    var countryIso = (fd.get('mobileCountry') || 'IN').toString();
    var country = countryByIso(countryIso);
    var dialCode = country ? country.dial : '91';
    var mobileNumber = '+' + dialCode + (fd.get('mobile') || '').toString().trim();
    var email = (fd.get('email') || '').toString().trim();
    // email isn't part of this call's payload (the API already has it from
    // _submitProfile/step 1) — read here only to compute the same
    // sourceSubmissionId that call established, via the same
    // email+mobile fingerprint (see getSessionSubmissionId's comment).
    // Neither field changes between steps (step 2 doesn't re-show them),
    // so this deterministically returns that already-stored id rather than
    // this step needing to carry it forward itself.
    var submissionId = getSessionSubmissionId(email, mobileNumber);
    var payload = {
      source: API_INTEGRATION_SOURCE,
      sourceSubmissionId: submissionId,
      registrant: { mobileNumber: mobileNumber },
      primaryMotivation: OPTION_API_VALUES[this.selectedOption],
      consent: {
        privacyPolicyAcceptedAt: new Date().toISOString(),
        sourceTextVersion: CONSENT_TEXT_VERSION
      }
    };

    var btn = this.submitAnswerBtn;
    var originalLabel = btn ? btn.textContent : '';
    if (this.submitError) this.submitError.hidden = true;
    if (btn) { btn.disabled = true; btn.textContent = 'Submitting…'; }

    // The button is already disabled the instant it's clicked (above), which
    // alone stops rapid re-clicking while a request is in flight. SUBMIT_TIMEOUT_MS
    // is only a ceiling for a hung request — once a real response (success or
    // error) comes back, act on it immediately rather than padding the wait.
    var controller = window.AbortController ? new AbortController() : null;
    var timeoutId = controller ? setTimeout(function () { controller.abort(); }, SUBMIT_TIMEOUT_MS) : null;

    fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-integration-source': API_INTEGRATION_SOURCE,
        'Idempotency-key': submissionId
      },
      body: JSON.stringify(payload),
      signal: controller ? controller.signal : undefined
    }).then(function (res) {
      if (timeoutId) clearTimeout(timeoutId);
      if (res.ok) {
        clearSessionSubmissionId();
        self._resetForm();
        if (btn) { btn.disabled = false; btn.textContent = originalLabel; }
        self._goToStep('done');
        return;
      }
      // 409 (duplicate email/mobile) is the one status whose response body
      // gets inspected — everything else keeps the prior behavior of a
      // single generic message, regardless of cause (other bad status,
      // network failure, or timeout) — except the specific VALIDATION_FAILED
      // shape isFieldValidationError checks for (a rejected email or mobile
      // number), which also gets its real message shown instead of the
      // generic one. Keyed off the body, not the status code, since the
      // API's actual status for this case isn't confirmed.
      if (res.status === 409) {
        return res.json().catch(function () { return null; }).then(function (data) {
          self._showSubmitError(data);
          if (btn) { btn.disabled = false; btn.textContent = originalLabel; }
        });
      }
      return res.json().catch(function () { return null; }).then(function (data) {
        self._showSubmitError(isFieldValidationError(data) ? data : null);
        if (btn) { btn.disabled = false; btn.textContent = originalLabel; }
      });
    }).catch(function () {
      if (timeoutId) clearTimeout(timeoutId);
      self._showSubmitError(null);
      if (btn) { btn.disabled = false; btn.textContent = originalLabel; }
    });
  };

  /* Wires the two halves of the dob field: the DD/MM/YYYY mask on the
   * visible text input, and the native calendar on the hidden companion
   * beside it (both declared in rr-application-modal.liquid).
   *
   * The field is type="text" on every browser because a native type="date"
   * is picker-only on mobile — there was no way to type a date there at
   * all. The calendar is still one tap away, just moved onto an input the
   * user never interacts with directly, so the typed and picked paths can
   * both end up in the same masked field. */
  RiseAndRest.prototype._bindDobField = function () {
    if (!this.form) return;
    var dobEl = this.form.elements['dob'];
    if (!dobEl) return;
    var self = this;
    var wrap = dobEl.parentNode;
    var nativeEl = wrap ? wrap.querySelector('[data-dob-native]') : null;
    var pickerBtn = wrap ? wrap.querySelector('[data-dob-picker]') : null;

    dobEl.addEventListener('input', function () {
      var before = dobEl.value;
      var next = maskDob(before);
      if (next === before) return;
      /* Assigning .value parks the caret at the end of the field, which is
       * only ever the right answer when the user is typing at the end. Note
       * where it was first (as a digit count, taken against the string the
       * user just produced), then put it back against the reformatted one —
       * otherwise editing mid-string flings the caret to the far right on
       * every keystroke. */
      var digitsBefore = countDigitsBefore(before, dobEl.selectionStart || 0);
      dobEl.value = next;
      var caret = offsetAfterDigits(next, digitsBefore);
      try {
        dobEl.setSelectionRange(caret, caret);
      } catch (e) {
        /* Field isn't focused (programmatic value change) — nothing to restore. */
      }
    });

    if (!nativeEl || !pickerBtn) return;

    /* No showPicker means no way to open a calendar we've hidden, so drop
     * the button rather than leave a control that does nothing. Typing
     * still works, which is the part that actually matters. */
    if (typeof nativeEl.showPicker !== 'function') {
      pickerBtn.hidden = true;
      return;
    }

    nativeEl.min = DOB_MIN_ISO;
    nativeEl.max = todayISO();

    pickerBtn.addEventListener('click', function () {
      /* Open on the date already typed, when it's a real one, so the
       * calendar continues the user's entry instead of resetting to today. */
      nativeEl.value = parseDob(dobEl.value) || '';
      try {
        nativeEl.showPicker();
      } catch (e) {
        /* Blocked (activation consumed, sandboxed frame, …) — the text
         * field takes the date perfectly well, so send the user there. */
        dobEl.focus();
      }
    });

    nativeEl.addEventListener('change', function () {
      /* Empty is a legitimate outcome here (the native picker's own clear
       * control), not just an unparseable value — it has to flow through to
       * the visible field the same as a picked date does, or "clear" would
       * silently leave the old typed value in place. */
      var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(nativeEl.value);
      dobEl.value = m ? m[3] + '/' + m[2] + '/' + m[1] : '';
      self._setFieldError(dobEl, '');
    });
  };
    /* Strips anything non-digit as the user types (also catches paste, since
   * `input` fires for that too), and caps length at the selected country's
   * max digit count (mobileMaxDigits — 10 for India by default, since
   * that's the field's starting country) rather than a fixed 10, now that
   * _bindPhoneCode lets the country change. */
  RiseAndRest.prototype._bindMobileField = function () {
    if (!this.form) return;
    var mobileEl = this.form.elements['mobile'];
    var countryEl = this.form.elements['mobileCountry'];
    if (!mobileEl) return;
    mobileEl.addEventListener('input', function () {
      var iso = (countryEl && countryEl.value) || 'IN';
      var digits = mobileEl.value.replace(/[^\d]/g, '').slice(0, mobileMaxDigits(iso));
      if (digits !== mobileEl.value) mobileEl.value = digits;
    });
  };

  /* Country calling-code picker for the phone field — a button (flag + dial
   * code) that opens a searchable popover built from window.RR_COUNTRY_CODES.
   * Selecting a country updates the visible flag/dial, the hidden
   * `mobileCountry` field FormData picks up for _submitApplication, and
   * re-caps the mobile input at that country's max digit length. Rendering
   * is JS-driven (list starts empty in the Liquid) the same way the
   * interest-question options are — see _renderOptions. */
  RiseAndRest.prototype._bindPhoneCode = function () {
    if (!this.form) return;
    var wrap = this.form.querySelector('[data-phone-code]');
    var mobileEl = this.form.elements['mobile'];
    var countryEl = this.form.elements['mobileCountry'];
    if (!wrap || !mobileEl || !countryEl) return;
    var self = this;
    var btn = wrap.querySelector('[data-phone-code-btn]');
    var menu = wrap.querySelector('[data-phone-code-menu]');
    var search = wrap.querySelector('[data-phone-code-search]');
    var list = wrap.querySelector('[data-phone-code-list]');
    var flagEl = btn.querySelector('[data-phone-flag]');
    var dialEl = btn.querySelector('[data-phone-dial]');
    var fieldControl = wrap.closest('.rr-field-control');
    var countries = (window.RR_COUNTRY_CODES || []).slice().sort(function (a, b) {
      return a.name.localeCompare(b.name);
    });
    var activeIndex = -1;

    /* Keeps the mobile input's left padding matched to the code button's
     * actual rendered width (flag + dial digits + arrow) so 3+ digit dial
     * codes (e.g. +971) don't push the dropdown arrow into the typed
     * digits — a fixed padding-left only ever fit 2-digit codes like +91.
     * Also re-run from _showModal: the modal is `display:none` until
     * opened, and offsetWidth reads 0 for anything inside a display:none
     * subtree, so the very first call here (made at bind time, modal still
     * closed) can't be trusted on its own. */
    function syncFieldPadding() {
      if (!fieldControl || !btn.offsetWidth) return;
      fieldControl.style.setProperty('--rr-phone-code-w', (btn.offsetWidth + 10) + 'px');
    }
    self._syncPhoneCodePadding = syncFieldPadding;
    // Custom theme fonts (rr-hero.liquid's font_* settings) can still be
    // loading when the modal first opens — the button gets measured against
    // whatever's rendering at that instant (system fallback), then visibly
    // grows once the real font swaps in, if it's wider for the same
    // characters. Re-measuring once fonts settle catches that; a no-op if
    // nothing was loading, or if the browser lacks the Font Loading API.
    if (window.document && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(syncFieldPadding);
    }

    function selectCountry(c) {
      countryEl.value = c.iso;
      flagEl.className = 'country-flags country-flags--' + c.iso;
      dialEl.textContent = '+' + c.dial;
      mobileEl.maxLength = mobileMaxDigits(c.iso);
      if (mobileEl.value.length > mobileEl.maxLength) {
        mobileEl.value = mobileEl.value.slice(0, mobileEl.maxLength);
      }
      self._setFieldError(mobileEl, '');
      syncFieldPadding();
    }
    // Exposed the same way _syncPhoneCodePadding is, so _resetForm can put
    // the code button back to the default country after a full reset —
    // form.reset() alone reverts the hidden mobileCountry input's value but
    // can't touch this button's rendered flag/dial text, which only this
    // closure knows how to redraw.
    self._selectPhoneCountry = selectCountry;

    syncFieldPadding();

    function renderList(query) {
      var q = (query || '').trim().toLowerCase();
      var rendered = !q
        ? countries
        : countries.filter(function (c) {
            return c.name.toLowerCase().indexOf(q) !== -1 || c.dial.indexOf(q.replace(/^\+/, '')) !== -1;
          });
      activeIndex = -1;
      list.innerHTML = '';
      if (!rendered.length) {
        var empty = document.createElement('li');
        empty.className = 'rr-phone-code__empty';
        empty.textContent = 'No matching country.';
        list.appendChild(empty);
        return;
      }
      rendered.forEach(function (c) {
        var li = document.createElement('li');
        li.setAttribute('role', 'presentation');
        var item = document.createElement('button');
        item.type = 'button';
        item.className = 'rr-phone-code__item';
        item.setAttribute('role', 'option');
        item.dataset.iso = c.iso;
        item.innerHTML =
          '<span class="country-flags country-flags--' + c.iso + '" aria-hidden="true"></span>' +
          '<span class="rr-phone-code__item-name">' + c.name + '</span>' +
          '<span class="rr-phone-code__item-dial">+' + c.dial + '</span>';
        item.addEventListener('click', function () {
          selectCountry(c);
          closeMenu();
        });
        li.appendChild(item);
        list.appendChild(li);
      });
    }

    function onDocClick(e) {
      if (!wrap.contains(e.target)) closeMenu();
    }

    function openMenu() {
      menu.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      search.value = '';
      renderList('');
      search.focus();
      document.addEventListener('click', onDocClick);
    }

    function closeMenu() {
      menu.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onDocClick);
    }

    function moveActive(delta) {
      var items = list.querySelectorAll('[data-iso]');
      if (!items.length) return;
      activeIndex = (activeIndex + delta + items.length) % items.length;
      items.forEach(function (el, i) { el.toggleAttribute('data-active', i === activeIndex); });
      items[activeIndex].scrollIntoView({ block: 'nearest' });
    }

    btn.addEventListener('click', function () {
      if (menu.hidden) openMenu(); else closeMenu();
    });

    search.addEventListener('input', function () { renderList(search.value); });

    menu.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        // Stops here — otherwise the modal's own document-level Escape
        // handler (see _bindModal) would also close the whole modal.
        e.stopPropagation();
        closeMenu();
        btn.focus();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveActive(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveActive(-1);
      } else if (e.key === 'Enter' && activeIndex > -1) {
        e.preventDefault();
        var el = list.querySelectorAll('[data-iso]')[activeIndex];
        if (el) el.click();
      }
    });
  };

  /* Strips anything but A-Z/a-z and spaces as the user types (also catches
   * paste) — spaces have to stay allowed or a first + last name could never
   * be typed as one value — and surfaces the same error message the rest of
   * the form uses so a stripped keystroke doesn't look like nothing
   * happened. The length cap comes from
   * the theme editor (data-name-max-length, wired to modal_name_max_length
   * in rr-hero.liquid's schema) and is only applied when
   * modal_name_enforce_limit is on — the native `maxlength` attribute
   * mirrors this in the markup so the cap holds even before JS runs, same
   * dual-layer approach as the mobile field's maxlength="10".
   *
   * This owns clearing its own error entirely (both this message and any
   * stale "Please enter your name." left over from onSubmit) rather than
   * going through the generic live-clear listener in init — that listener
   * only clears once the field is already valid, which would immediately
   * wipe this message the same tick it's set, since the value is valid
   * *after* stripping. #rr-full-name is deliberately left out of that
   * listener's selector for this reason. */
  RiseAndRest.prototype._bindNameField = function () {
    if (!this.form) return;
    var nameEl = this.form.elements['fullName'];
    if (!nameEl) return;
    var self = this;
    var enforceLimit = nameEl.dataset.nameEnforceLimit === 'true';
    var maxLength = parseInt(nameEl.dataset.nameMaxLength, 10);
    var hasLimit = enforceLimit && maxLength > 0;
    var wrap = nameEl.closest('.rr-field-wrap');
    var counterEl = wrap ? wrap.querySelector('[data-name-counter]') : null;
    /* Only meaningful when there's an actual enforced cap to count toward —
     * with the toggle off there's nothing for "12/50" to mean, so the
     * counter stays hidden rather than showing a limit that isn't real. */
    if (counterEl && hasLimit) {
      counterEl.hidden = false;
      counterEl.textContent = nameEl.value.length + '/' + maxLength;
    }
    nameEl.addEventListener('input', function () {
      var raw = nameEl.value;
      var letters = raw.replace(/[^A-Za-z ]/g, '');
      var hadInvalidChar = letters !== raw;
      if (hasLimit) letters = letters.slice(0, maxLength);
      if (letters !== nameEl.value) nameEl.value = letters;
      /* Invalid-character feedback wins when both are true (e.g. typing a
       * symbol while already at the cap) — that's the more actionable
       * message of the two. The limit message otherwise covers both hitting
       * the cap exactly and native maxlength silently swallowing further
       * keystrokes, since there's no input event to react to for the latter. */
      if (hadInvalidChar) self._setFieldError(nameEl, NAME_INVALID_CHAR_MESSAGE);
      else if (hasLimit && nameEl.value.length >= maxLength) self._setFieldError(nameEl, NAME_LIMIT_REACHED_MESSAGE);
      else if (nameEl.value) self._setFieldError(nameEl, '');
      if (counterEl && hasLimit) counterEl.textContent = nameEl.value.length + '/' + maxLength;
    });
  };


  RiseAndRest.prototype._setAudience = function (value) {
    this.audience = value;
    var pickMe = this.modal.querySelector('[data-pick-me]');
    var pickOther = this.modal.querySelector('[data-pick-other]');
    if (pickMe) pickMe.setAttribute('aria-pressed', value === 'me' ? 'true' : 'false');
    if (pickOther) pickOther.setAttribute('aria-pressed', value === 'other' ? 'true' : 'false');
  };

  RiseAndRest.prototype._renderOptions = function () {
    var self = this;
    var list = this.modal.querySelector('[data-question-options]');
    if (!list) return;
    list.innerHTML = '';
    this.selectedOption = null;
    OPTION_LABELS.forEach(function (label, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rr-question__option';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
      btn.dataset.index = i;
      btn.innerHTML = '<span class="rr-question__radio" aria-hidden="true"><span class="rr-question__radio-dot"></span></span>' +
        '<span class="rr-question__option-label">' + label + '</span>';
      btn.addEventListener('click', function () {
        Array.prototype.slice.call(list.children).forEach(function (b) { b.setAttribute('aria-checked', 'false'); });
        btn.setAttribute('aria-checked', 'true');
        self.selectedOption = i;
        if (self.questionError) self.questionError.hidden = true;
      });
      list.appendChild(btn);
    });
  };

  /* Puts the modal back to the state a freshly-loaded page would render,
   * run once an application has gone all the way through to the "done"
   * step (see the success branch of _submitApplication) — so reopening the
   * modal afterwards starts blank instead of showing the previous
   * applicant's details. Deliberately not called from closeModal/openModal:
   * abandoning a still-in-progress form on close shouldn't discard what was
   * typed, only a completed one should. */
  RiseAndRest.prototype._resetForm = function () {
    var self = this;
    if (this.form) {
      this.form.reset();
      ['fullName', 'email', 'mobile', 'dob', 'social'].forEach(function (name) {
        var el = self.form.elements[name];
        if (el) self._setFieldError(el, '');
      });
      // Re-runs _bindNameField's own input handler so the character counter
      // (which only updates on 'input', not on form.reset()'s silent value
      // change) goes back to "0/N" instead of showing the stale count.
      var nameEl = this.form.elements['fullName'];
      if (nameEl) nameEl.dispatchEvent(new Event('input'));
    }
    if (this._selectPhoneCountry) this._selectPhoneCountry(countryByIso('IN'));
    this._setAudience('me');
    this._renderOptions();
    // terms2 and the question options live in step 2's markup, outside
    // <form data-rr-form> (see rr-application-modal.liquid), so form.reset()
    // above never reaches them.
    if (this.termsCheckbox) this.termsCheckbox.checked = false;
    if (this.questionError) this.questionError.hidden = true;
    if (this.consentError) this.consentError.hidden = true;
    if (this.formSubmitError) {
      this.formSubmitError.hidden = true;
      this.formSubmitError.textContent = this.defaultFormSubmitErrorText;
    }
    if (this.submitError) {
      this.submitError.hidden = true;
      this.submitError.textContent = this.defaultSubmitErrorText;
    }
  };

  RiseAndRest.prototype._announce = function (msg) {
    if (this.liveRegion) this.liveRegion.textContent = msg;
  };

  RiseAndRest.prototype.openModal = function (e) {
    var self = this;
    var sx = e && e.clientX ? e.clientX : window.innerWidth / 2;
    var sy = e && e.clientY ? e.clientY : window.innerHeight / 2;
    document.documentElement.style.setProperty('--sx', sx + 'px');
    document.documentElement.style.setProperty('--sy', sy + 'px');

    this._goToStep('form');
    clearTimeout(this._splashT1);
    clearTimeout(this._splashT2);

    if (this.splash && !this.reducedMotion) {
      this.splash.setAttribute('data-open', '');
      this._splashT1 = setTimeout(function () { self._showModal(); }, 300);
      this._splashT2 = setTimeout(function () { self.splash.removeAttribute('data-open'); }, 1100);
    } else {
      this._showModal();
    }
  };

  RiseAndRest.prototype._showModal = function () {
    this.modal.setAttribute('data-open', '');
    document.body.classList.add('rr-modal-open');
    // Now that the modal is actually laid out, the phone code button has a
    // real width to measure — see syncFieldPadding in _bindPhoneCode.
    if (this._syncPhoneCodePadding) this._syncPhoneCodePadding();
    // Focus the first real form field, not the close button — querying
    // '.rr-modal__body' (the form/steps area) excludes the close button,
    // which otherwise matches first since it precedes the form in the DOM
    // and was picking up a visible focus ring on open.
    var firstField = this.modal.querySelector('.rr-modal__body input, .rr-modal__body button');
    if (firstField) firstField.focus();
    this._announce('Dialog opened: Apply for Priority Access');
  };

  RiseAndRest.prototype.closeModal = function () {
    this.modal.removeAttribute('data-open');
    document.body.classList.remove('rr-modal-open');
    if (this.lastTrigger) this.lastTrigger.focus();
  };

  RiseAndRest.prototype._trapFocus = function (e) {
    var focusable = this.modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  RiseAndRest.prototype._goToStep = function (step) {
    [this.stepForm, this.stepQuestion, this.stepDone].forEach(function (el) { if (el) el.removeAttribute('data-active'); });
    var target = step === 'form' ? this.stepForm : step === 'question' ? this.stepQuestion : this.stepDone;
    if (target) target.setAttribute('data-active', '');
    // Read by the per-step/per-breakpoint wave-toggle CSS in
    // rise-and-rest.css (.rr-modal[data-modal-step="..."] .rr-wave-hide-...).
    this.modal.setAttribute('data-modal-step', step);
    // Banner: title + copy + perks on the form step, title only on the
    // question step, hidden entirely once done — ported from the
    // prototype's own showFormBanner ('form' only) / bannerDisplay
    // (hidden once 'done') logic.
    if (this.modalBanner) this.modalBanner.style.display = step === 'done' ? 'none' : '';
    if (this.modalUnderline) this.modalUnderline.style.display = step === 'done' ? 'none' : '';
    if (this.bannerCopy) this.bannerCopy.style.display = step === 'form' ? '' : 'none';
    if (this.bannerPerks) this.bannerPerks.style.display = step === 'form' ? '' : 'none';
    var labels = { form: 'Step 1 of 2: application form', question: 'Step 2 of 2: interest question', done: 'Application received' };
    this._announce(labels[step] || '');
  };

  /* Format-checks a single field in place (used on blur, ahead of submit).
   * Only flags a real mismatch — an empty field isn't treated as an error
   * here, so the red state doesn't appear before the user has typed
   * anything; emptiness itself is still caught by onSubmit's required check. */
  /* Toggles both the red border and a small explanatory message under the
   * field, so a failed field doesn't just turn red with no indication of
   * what's wrong with it. */
  RiseAndRest.prototype._setFieldError = function (el, message) {
    if (!el) return;
    el.classList.toggle('rr-field--error', !!message);
    var wrap = el.closest('.rr-field-wrap');
    var msgEl = wrap ? wrap.querySelector('[data-field-error]') : null;
    if (msgEl) {
      msgEl.textContent = message || '';
      msgEl.hidden = !message;
    }
  };

  /* Single source of truth for "is this field's current value acceptable",
   * shared by onSubmit and the live-clear listener below — previously each
   * kept its own copy of these regexes, which is how they drifted (onSubmit
   * checked dob/social, _validateField didn't). Empty counts as invalid here
   * since this is the full check; callers that shouldn't flag emptiness
   * (e.g. blur) guard for that themselves before calling in.
   *
   * Return value doubles as the error reason: `false`/`null` is valid,
   * `true` is invalid with no specific reason (caller falls back to
   * FIELD_ERROR_MESSAGES), and a string is invalid with that exact message
   * to show instead of the generic one — see the email "before @" check
   * below for the one case that currently uses this. */
  RiseAndRest.prototype._fieldInvalid = function (el) {
    var val = (el.value || '').trim();
    // Optional: blank is valid, but whatever's typed still has to be a real
    // LinkedIn URL — checked ahead of the blanket empty-check below, which
    // every other field is still subject to.
    if (el.name === 'social') return val ? !LINKEDIN_URL_RE.test(val) : false;
    if (!val) return true;
    if (el.name === 'email') {
      if (val.indexOf('@') === -1) {
        return 'Please include an "@" in the email address.';
      }
      // The character right before "@" must be a letter/digit — the API
      // rejects e.g. "name.@gmail.com" ("Please enter a correct email ID.")
      // even though the old [^@\s]+ pattern let a trailing dot/dash/etc.
      // through. Checked ahead of the general format regex so this specific
      // reason isn't masked by the generic "invalid email" message.
      if (!/^[^@\s]*[A-Za-z0-9]@/.test(val)) {
        return "The character right before \"@\" can't be a special character.";
      }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val)) return true;
      if (!emailEndingAllowed(el, val)) return emailEndingErrorMessage(el);
      return false;
    }
    if (el.name === 'mobile') {
      var countryEl = this.form && this.form.elements['mobileCountry'];
      var iso = (countryEl && countryEl.value) || 'IN';
      return !mobileDigitsValid(iso, val.replace(/\D/g, ''));
    }
    if (el.name === 'dob') return !dobToISO(el);
    return false;
  };

  /* invalid is _fieldInvalid's return value — a string is shown verbatim,
   * `true` falls back to the field's generic message, anything falsy clears
   * the error. Centralized here so onSubmit and _validateField (both below)
   * apply that convention identically. */
  RiseAndRest.prototype._fieldErrorMessage = function (name, invalid) {
    if (typeof invalid === 'string') return invalid;
    return invalid ? FIELD_ERROR_MESSAGES[name] : '';
  };

  RiseAndRest.prototype._validateField = function (el) {
    var val = (el.value || '').trim();
    var invalid = false;
    if (val && (el.name === 'email' || el.name === 'mobile')) invalid = this._fieldInvalid(el);
    this._setFieldError(el, this._fieldErrorMessage(el.name, invalid));
    return !invalid;
  };

  RiseAndRest.prototype.onSubmit = function (e) {
    e.preventDefault();
    var f = this.form;
    if (!f) return;
    var required = ['fullName', 'email', 'mobile', 'dob', 'social'];
    var firstInvalidEl = null;
    for (var i = 0; i < required.length; i++) {
      var n = required[i];
      var el = f.elements[n];
      var invalid = this._fieldInvalid(el);
      this._setFieldError(el, this._fieldErrorMessage(n, invalid));
      if (invalid && !firstInvalidEl) firstInvalidEl = el;
    }
    if (firstInvalidEl) { firstInvalidEl.focus(); return; }
    this._submitProfile();
  };

  /* Step 1 → step 2. Saves the registrant's profile (name/email/mobile/dob/
   * LinkedIn) + interestFor to the same endpoint _submitApplication (step 2,
   * below) later posts primaryMotivation/consent to — the two are separate
   * requests sharing one sourceSubmissionId (see getSessionSubmissionId),
   * not one combined payload. Only advances to the question step on
   * success, so the user can't reach step 2 with a profile that was never
   * actually saved. */
  RiseAndRest.prototype._submitProfile = function () {
    var self = this;
    if (!this.form) return;
    var fd = new FormData(this.form);
    var countryIso = (fd.get('mobileCountry') || 'IN').toString();
    var country = countryByIso(countryIso);
    var dialCode = country ? country.dial : '91';
    var registrant = {
      name: (fd.get('fullName') || '').toString().trim(),
      mobileNumber: '+' + dialCode + (fd.get('mobile') || '').toString().trim(),
      email: (fd.get('email') || '').toString().trim(),
      dateOfBirth: dobToISO(this.form.elements['dob'])
    };
    // Optional field (see _fieldInvalid) — omitted entirely rather than
    // sent as "" when blank, matching the shape the API expects for "not
    // provided".
    var linkedin = (fd.get('social') || '').toString().trim();
    if (linkedin) registrant.linkedinProfileUrl = linkedin;
    var submissionId = getSessionSubmissionId(registrant.email, registrant.mobileNumber);
    var payload = {
      source: API_INTEGRATION_SOURCE,
      sourceSubmissionId: submissionId,
      registrant: registrant,
      interestFor: this.audience === 'other' ? 'SOMEONE_ELSE' : 'SELF'
    };

    var btn = this.continueBtn;
    var originalLabel = btn ? btn.textContent : '';
    if (this.formSubmitError) this.formSubmitError.hidden = true;
    if (btn) { btn.disabled = true; btn.textContent = 'Submitting…'; }

    var controller = window.AbortController ? new AbortController() : null;
    var timeoutId = controller ? setTimeout(function () { controller.abort(); }, SUBMIT_TIMEOUT_MS) : null;

    fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-integration-source': API_INTEGRATION_SOURCE,
        'Idempotency-key': submissionId
      },
      body: JSON.stringify(payload),
      signal: controller ? controller.signal : undefined
    }).then(function (res) {
      if (timeoutId) clearTimeout(timeoutId);
      if (res.ok) {
        if (btn) { btn.disabled = false; btn.textContent = originalLabel; }
        self._goToStep('question');
        return;
      }
      // 409 (duplicate email/mobile) is the one status whose response body
      // gets inspected — everything else keeps the prior behavior of a
      // single generic message — except the isFieldValidationError shape,
      // which also gets its real message shown instead of the generic one
      // (see the matching comment in _submitApplication).
      if (res.status === 409) {
        return res.json().catch(function () { return null; }).then(function (data) {
          self._showFormSubmitError(data);
          if (btn) { btn.disabled = false; btn.textContent = originalLabel; }
        });
      }
      return res.json().catch(function () { return null; }).then(function (data) {
        self._showFormSubmitError(isFieldValidationError(data) ? data : null);
        if (btn) { btn.disabled = false; btn.textContent = originalLabel; }
      });
    }).catch(function () {
      if (timeoutId) clearTimeout(timeoutId);
      self._showFormSubmitError(null);
      if (btn) { btn.disabled = false; btn.textContent = originalLabel; }
    });
  };

  /* Mirrors _showSubmitError's 409 detail-parsing, but step 1 already has
   * inline per-field error spans for email/mobile/social — so a matched
   * field's message goes there instead of a generic banner. Only falls back
   * to formSubmitError for failures that don't name one of those fields
   * (network error, 5xx, timeout, or a 409/VALIDATION_FAILED whose details
   * don't mention any of them). */
  RiseAndRest.prototype._showFormSubmitError = function (data) {
    var err = data && data.error;
    var details = err && err.details;
    var handledField = false;
    if (details && details.length) {
      for (var i = 0; i < details.length; i++) {
        var field = (details[i].field || '').toLowerCase();
        var el = null;
        if (field.indexOf('email') !== -1) el = this.form.elements['email'];
        else if (field.indexOf('mobile') !== -1 || field.indexOf('phone') !== -1) el = this.form.elements['mobile'];
        else if (field.indexOf('linkedin') !== -1 || field.indexOf('social') !== -1) el = this.form.elements['social'];
        if (el && details[i].message) {
          this._setFieldError(el, details[i].message);
          handledField = true;
        }
      }
    }
    if (handledField) return;
    if (!this.formSubmitError) return;
    // A flat err.message here is usually the same kind of user-safe
    // duplicate-registrant text _showSubmitError trusts (e.g. "this email
    // is already registered"), so pass it through by default. An
    // idempotency-key conflict is really the same situation in practice
    // (same email+mobile already has a submission in flight/on record —
    // see getSessionSubmissionId), it's just worded as an internal
    // replay-dedup detail — so reword it instead of showing it raw.
    var msg = err && err.message;
    var isIdempotencyLeak = msg && /idempotency/i.test(msg);
    this.formSubmitError.textContent = isIdempotencyLeak
      ? 'You are already registered with this email and mobile number.'
      : (msg || this.defaultFormSubmitErrorText);
    this.formSubmitError.hidden = false;
  };

  /* Safari, Firefox and Opera have two separate quirks that Chrome doesn't:
   * (1) they only honor the `muted` HTML attribute unreliably for autoplay
   * purposes — they also need the `.muted` IDL property set via JS, or
   * autoplay is silently blocked; (2) WebKit in particular can silently
   * flip `video.controls` back to `true` on its own `play`/`pause` state
   * changes, overriding `controls: false` from the video_tag output, which
   * is what shows the native play/pause bar even though our markup never
   * requested it. Re-asserting `controls = false` on every play/pause event
   * is the confirmed workaround (Shopify community). */
  function initHeroVideoAutoplay() {
    var videos = document.querySelectorAll('.rr-hero__media video');
    for (var i = 0; i < videos.length; i++) {
      var v = videos[i];
      v.controls = false;
      v.defaultMuted = true;
      v.muted = true;
      v.setAttribute('webkit-playsinline', 'true');
      v.setAttribute('playsinline', 'true');
      v.removeAttribute('controls');
      v.addEventListener('play', function () { this.controls = false; this.removeAttribute('controls'); });
      v.addEventListener('pause', function () { this.controls = false; this.removeAttribute('controls'); });
      if (!v.hasAttribute('autoplay')) continue;
      var playPromise = v.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
    }
  }

  function init() {
    if (!document.querySelector('.rr-scope')) return;
    window.__riseAndRest = new RiseAndRest();
    initHeroVideoAutoplay();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
